import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type InboxMessage } from '../lib/api.js';
import { useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { formatTime } from '../lib/time.js';

/**
 * The staff inbox.
 *
 * Free text a guest sent that a host has to answer. Deliberately NOT a
 * conversation engine — that is a later phase, and the seam is exactly here.
 *
 * The badge on each row is the thing that stops a wasted message: if the guest's
 * 24-hour window has closed, a free-form reply will fail at Meta with 131047
 * and the guest will never see it. Rather than offering a text box that
 * silently does nothing, the row says so and points at the alternatives.
 */
export function InboxScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const queryClient = useQueryClient();

  const inbox = useQuery({
    queryKey: ['inbox', venueId],
    enabled: Boolean(venueId),
    queryFn: () =>
      api.get<{ messages: InboxMessage[] }>('/api/v1/inbox', { venue_id: venueId }),
    refetchInterval: 30_000,
  });

  const markHandled = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/inbox/${id}/handled`, { venue_id: venueId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const messages = inbox.data?.messages ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('inbox.title')}</h1>

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {messages.map((message) => (
          <li key={message.id} className="flex flex-col gap-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {[message.first_name, message.last_name].filter(Boolean).join(' ') ||
                  t('common.guest')}
              </span>
              {message.booking_ref ? (
                <span className="font-mono text-xs text-slate-500">{message.booking_ref}</span>
              ) : null}
              <span className="font-mono text-xs text-slate-400" data-numeric>
                {formatTime(message.created_at, venue, i18n.language)}
              </span>

              <span
                className={[
                  'rounded-full px-2 py-0.5 text-xs',
                  message.can_reply_freely
                    ? 'bg-brand-teal-light text-brand-teal-dark'
                    : 'bg-amber-50 text-amber-800',
                ].join(' ')}
              >
                {message.can_reply_freely ? t('inbox.windowOpen') : t('inbox.windowClosed')}
              </span>
            </div>

            <p className="text-sm text-slate-800">{message.body_rendered}</p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => markHandled.mutate(message.id)}
                className="rounded-md border border-slate-200 px-3 py-1 text-sm hover:bg-brand-teal-light"
              >
                {t('inbox.markHandled')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {messages.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {t('inbox.empty')}
        </p>
      ) : null}
    </div>
  );
}
